import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { buscarSituacaoPresencial, type SituacaoPresencial } from '@/lib/presencial';
import PrimaryButton from '@/components/ui/PrimaryButton';
import SecondaryButton from '@/components/ui/SecondaryButton';

interface Props {
  codigo: string;
  nome: string;
  eventoNome: string;
  situacaoInicial: SituacaoPresencial;
  /** Modalidade que entra pela senha da fila (sem QR). */
  usaSenha?: boolean;
  senha?: string | null;
  /** Valor da inscrição em centavos (para a frase de pagamento). */
  valorCentavos?: number;
  /** Situação da inscrição (pendente/confirmada…). */
  status?: string | null;
  /** A foto da ficha não subiu: avisa com uma linha discreta. */
  semFoto?: boolean;
  aoFicha?: () => void;
  aoConcluir: (resultado: Estado) => void;
}

interface Estado {
  situacao: SituacaoPresencial;
  ordem: number | null;
  posicao_espera: number | null;
  validada_em: string | null;
}

const INTERVALO_MS = 4_000;

// Cores do papel picado (só para o "Vaga garantida!").
const CORES_CONFETE = [
  'bg-primary-500',
  'bg-accent-500',
  'bg-success-500',
  'bg-warning-400',
  'bg-secondary-400',
];

// Tela presencial: no modo QR espera a direção bipar; no modo senha da fila mostra
// a vaga/suplente pela senha que a pessoa informou na ficha (sem QR).
export default function TelaPresencial({
  codigo,
  nome,
  eventoNome,
  situacaoInicial,
  usaSenha = false,
  senha: senhaProp = null,
  valorCentavos = 0,
  status = null,
  semFoto = false,
  aoFicha,
  aoConcluir,
}: Props) {
  const [estado, setEstado] = useState<Estado>({
    situacao: situacaoInicial,
    ordem: null,
    posicao_espera: null,
    validada_em: null,
  });
  // Guardamos a senha para a tela da direção; a tela da pessoa não a mostra.
  const [, setSenha] = useState<string | null>(senhaProp);
  const [confete, setConfete] = useState(false);
  const jaComemorou = useRef(false);

  // Busca o estado atual uma vez (pega a ordem/senha que não vêm na carga inicial).
  useEffect(() => {
    let ativo = true;
    buscarSituacaoPresencial(codigo)
      .then((s) => {
        if (!ativo || !s) return;
        setEstado((e) => ({
          ...e,
          situacao: s.situacao ?? e.situacao,
          ordem: s.ordem,
          posicao_espera: s.posicao_espera,
          validada_em: s.validada_em ?? e.validada_em,
        }));
        if (s.senha) setSenha(s.senha);
      })
      .catch(() => {
        /* mantém o estado inicial */
      });
    return () => {
      ativo = false;
    };
  }, [codigo]);

  // No modo QR, enquanto aguardando, pergunta de novo a cada 4 s.
  useEffect(() => {
    if (usaSenha || estado.situacao !== 'aguardando') return;
    let ativo = true;
    const timer = setInterval(async () => {
      try {
        const s = await buscarSituacaoPresencial(codigo);
        if (!ativo || !s) return;
        setEstado((e) => ({
          ...e,
          situacao: s.situacao ?? e.situacao,
          ordem: s.ordem,
          posicao_espera: s.posicao_espera,
          validada_em: s.validada_em ?? e.validada_em,
        }));
        if (s.senha) setSenha(s.senha);
      } catch {
        /* sem sinal — tenta de novo no próximo tique */
      }
    }, INTERVALO_MS);
    return () => {
      ativo = false;
      clearInterval(timer);
    };
  }, [codigo, estado.situacao, usaSenha]);

  // Comemora uma vez, quando vira vaga.
  useEffect(() => {
    if (estado.situacao === 'vaga' && !jaComemorou.current) {
      jaComemorou.current = true;
      setConfete(true);
      const t = setTimeout(() => setConfete(false), 1800);
      return () => clearTimeout(t);
    }
  }, [estado.situacao]);

  const particulas = useMemo(
    () =>
      Array.from({ length: 16 }).map((_, i) => ({
        left: `${6 + (i * 88) / 16}%`,
        delay: `${(i % 6) * 90}ms`,
        cor: CORES_CONFETE[i % CORES_CONFETE.length],
      })),
    [],
  );

  // -------------------------------------------------------------------------
  // Modo senha da fila (sem QR).
  // -------------------------------------------------------------------------
  if (usaSenha && estado.situacao === 'aguardando') {
    return (
      <div className="min-h-screen bg-background-100 pb-10">
        <main className="mx-auto flex w-full max-w-[560px] flex-col px-5 pt-12">
          <div className="text-center">
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-warning-100 text-warning-700">
              <i className="ri-ticket-2-line text-[42px]" aria-hidden />
            </span>
            <h1 className="mt-4 text-[22px] font-extrabold leading-tight text-foreground-950">
              Falta informar a senha da fila
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground-600">
              Termine a ficha e, no último item, informe a senha que você recebeu na fila.
            </p>
          </div>

          {aoFicha && (
            <PrimaryButton cheio className="mt-8" icone="ri-edit-line" onClick={aoFicha}>
              Preencher minha ficha
            </PrimaryButton>
          )}
          <SecondaryButton cheio className="mt-3" onClick={() => aoConcluir(estado)}>
            Ver minha inscrição
          </SecondaryButton>
        </main>
      </div>
    );
  }

  // Fim da inscrição (Campista): mensagem única, sem falar em vaga/suplente.
  if (usaSenha && (estado.situacao === 'vaga' || estado.situacao === 'lista_espera')) {
    const confirmada = status === 'confirmada' || status === 'isenta';
    return (
      <div className="min-h-screen bg-background-100">
        <main className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 pt-16 text-center">
          <span className="flex h-24 w-24 items-center justify-center rounded-full bg-success-500 text-white">
            <i className="ri-heart-3-fill text-[48px]" aria-hidden />
          </span>
          <h1 className="mt-5 text-[26px] font-extrabold leading-tight text-foreground-950">
            Deus te abençoe!
          </h1>
          <p className="mt-3 max-w-[420px] text-[16px] leading-relaxed text-foreground-700">
            {eventoNome ? `Você está inscrito no ${eventoNome}.` : 'Você está inscrito.'}
          </p>
          {!confirmada && valorCentavos > 0 && (
            <p className="mt-4 text-[14px] text-foreground-500">Pagamento: com a direção</p>
          )}
          {semFoto && (
            <p className="mt-3 max-w-[420px] text-[13px] leading-relaxed text-foreground-500">
              A foto não foi enviada — dá para mandar depois em Minhas inscrições.
            </p>
          )}
          <PrimaryButton cheio className="mt-8" onClick={() => aoConcluir(estado)}>
            Ver minha inscrição
          </PrimaryButton>
        </main>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Modo QR (como hoje).
  // -------------------------------------------------------------------------
  if (estado.situacao === 'aguardando') {
    return (
      <div className="min-h-screen bg-background-100 pb-10">
        <main className="mx-auto flex w-full max-w-[560px] flex-col px-5 pt-8">
          <div className="text-center">
            <h1 className="text-[24px] font-extrabold leading-tight text-foreground-950">
              Mostre este QR para a direção
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-foreground-600">
              Sua vaga só vale depois que a direção bipar. A fila é a ordem da bipagem.
            </p>
          </div>

          <div className="mt-6 flex justify-center">
            <div className="rounded-[22px] bg-background-50 p-5 shadow-pcard">
              <QRCode value={codigo} size={248} />
            </div>
          </div>

          {nome && (
            <p className="mt-5 text-center text-[16px] font-bold text-foreground-950">{nome}</p>
          )}
          {eventoNome && (
            <p className="mt-0.5 text-center text-[14px] text-foreground-500">{eventoNome}</p>
          )}

          <div className="mt-6 flex items-center justify-center gap-2 rounded-[18px] bg-warning-50 px-4 py-3">
            <span className="flex h-5 w-5 items-center justify-center text-warning-600">
              <i className="ri-time-line text-lg" aria-hidden />
            </span>
            <span className="text-[15px] font-semibold text-warning-800">Aguardando validação</span>
          </div>
        </main>
      </div>
    );
  }

  if (estado.situacao === 'vaga') {
    return (
      <div className="relative min-h-screen overflow-hidden bg-background-100">
        {confete && (
          <div className="pointer-events-none absolute inset-0 z-10" aria-hidden>
            {particulas.map((p, i) => (
              <span
                key={i}
                className={`confete absolute top-0 h-3 w-1.5 rounded-sm ${p.cor}`}
                style={{ left: p.left, animationDelay: p.delay }}
              />
            ))}
          </div>
        )}
        <main className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 pt-16 text-center">
          <span className="check-pop flex h-24 w-24 items-center justify-center rounded-full bg-success-500 text-white shadow-pbtn">
            <i className="ri-check-line text-[52px]" aria-hidden />
          </span>
          <h1 className="mt-5 text-[24px] font-extrabold leading-tight text-foreground-950">
            Vaga garantida!
          </h1>
          <p className="mt-1 text-[17px] font-bold text-success-700">
            {estado.ordem != null ? `Você é o nº ${estado.ordem}` : 'Sua vaga está reservada'}
          </p>
          <PrimaryButton cheio className="mt-8" onClick={() => aoConcluir(estado)}>
            Ver minha inscrição
          </PrimaryButton>
        </main>
      </div>
    );
  }

  if (estado.situacao === 'lista_espera') {
    return (
      <div className="min-h-screen bg-background-100">
        <main className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 pt-16 text-center">
          <span className="flex h-24 w-24 items-center justify-center rounded-full bg-warning-500 text-white">
            <i className="ri-time-line text-[52px]" aria-hidden />
          </span>
          <h1 className="mt-5 text-[24px] font-extrabold leading-tight text-foreground-950">
            Lista de espera
          </h1>
          <p className="mt-1 text-[16px] font-bold text-warning-700">
            {estado.posicao_espera != null ? `Você é o ${estado.posicao_espera}º` : 'Você está na fila'}
          </p>
          <PrimaryButton cheio className="mt-8" onClick={() => aoConcluir(estado)}>
            Ver minha inscrição
          </PrimaryButton>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <main className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 pt-16 text-center">
        <span className="flex h-24 w-24 items-center justify-center rounded-full bg-secondary-400 text-white">
          <i className="ri-close-line text-[52px]" aria-hidden />
        </span>
        <h1 className="mt-5 text-[24px] font-extrabold leading-tight text-foreground-950">
          Inscrição cancelada
        </h1>
        <PrimaryButton cheio className="mt-8" onClick={() => aoConcluir(estado)}>
          Ver minha inscrição
        </PrimaryButton>
      </main>
    </div>
  );
}